import { HttpErrorResponse } from '@angular/common/http';
import { IR2ChatHistoryItem, IR2GetMessagesResponse, R2ApiResult, R2AttachmentType } from "@common/types/api/r2-types";
import { IChatActionList, IChatActionsData } from "@core/base/chat-action-base";
import { ISessionInfo } from "@core/base/session-base.types";
import { SubscriptionBase } from "@core/base/subscription-base";
import { chatConstants } from '@core/constants/chat.constants';
import { ICoreDependencies } from "@core/core-boot/core-boot.types";
import { OfficeChatRepository } from "@core/office/office-chat-repository/office-chat-repository";
import { OfficeChatSetting } from "@core/office/office-chat-setting/office-chat-setting";
import { OfficeChatBase } from "@core/office/office-chat/office-chat-base";
import { OfficeChatAdapterV1Repository } from "@core/office/office-chat/office-chat-v1/office-chat-adapter-v1-repository";
import { IChatOfficeMessage } from "@core/office/office-chat/office-chat-v1/office-chat-v1.types";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import { OfficeGroupChat } from '@core/office/office-group-chat/office-group-chat';
import { ChatUtilsService } from "@core/shared/chat-utils.service";
import type { IChatMessage } from '@core/types/chat-message.types';
import type { IChatMessageResult, IGuid } from '@core/types/chat.types';
import { environment } from "@env";
import { CryptoChannel } from '@services/crypto';
import { logger } from "@services/logger/logger.service";
import { describeError } from "@utils/error";
import { BehaviorSubject, Observable, firstValueFrom, map } from "rxjs";
import { v4 as uuidV4 } from 'uuid';
import { ISkillQueueProgress, OfficeChatV1 } from "./office-chat-v1";
import { OfficeDocumentThreadManager } from './office-document-thread-manager';

const coreConstants = chatConstants;
// v2: Word/Excel/PowerPoint document ids moved from a per-client settings-UID to
// a hash of the file's own url (see BaseHost.resolveFileUrlDocumentIDAsync), so
// the same document now resolves to the same id on Office web and desktop.
// Bumping this replays the existing assign-thread-id migration once per client,
// reassigning any thread created under the old id to the new one.
const THREAD_MIGRATION_VERSION = 2;

interface IThreadMigrationResult
{
    threadId: string | null;
    shouldFinalize: boolean;
    blocked: boolean;
}

interface IOfficeMessagesInitResult
{
    serverHistory: IR2ChatHistoryItem[];
    loadedFromServer: boolean;
}

export class OfficeChatAdapterServiceV1 extends OfficeChatV1 implements OfficeChatBase
{
    public constants = chatConstants;
    public cryptoServiceRepository = new CryptoChannel();
    private chatRepositoryCrypto = new OfficeChatAdapterV1Repository(this.chatUtils, this.cryptoServiceRepository);
    private isMessagesLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private officeService = this.office;

    constructor(
        private dependencies: ICoreDependencies,
        private office: OfficeService,
        private chatRepository: OfficeChatRepository,
        private chatUtils: ChatUtilsService,
        settingService: OfficeChatSetting,
        sessionService: OfficeSessionService,
        subscriptionService: SubscriptionBase,
        groupChat?: OfficeGroupChat,
    )
    {
        super(
            dependencies.http,
            settingService,
            sessionService,
            dependencies.aiModels,
            subscriptionService,
            dependencies.attachmentStore,
            dependencies.userSettingsApi,
            dependencies.sessionCrypto,
            new OfficeDocumentThreadManager(sessionService, office, dependencies.sessionCrypto),
            dependencies.translate,
            dependencies.instructions,
            office,
            groupChat,
        );
        this.defineHandlerForDocumentChange();
        this.defineHandlerForSessionChange();
    }

    public $isMessagesLoading(): Observable<boolean>
    {
        return this.isMessagesLoading.asObservable();
    }

    public $skillQueueProgress(): Observable<ISkillQueueProgress | null>
    {
        return this.skillQueueProgress.asObservable();
    }

    public getMessages(): Observable<IChatMessage[]>
    {
        return this.$messages.pipe(map(data =>
        {
            return this.convertOfficeMessagesToMessages(data);
        }));
    }

    public getMessagesValue(): IChatMessage[]
    {
        const officeMessages = this.$messages.getValue();
        return this.convertOfficeMessagesToMessages(officeMessages);
    }

    public async sendMessage(text: string, dataAction: IChatActionsData | null, actions: IChatActionList, retry = false): Promise<IChatMessageResult>
    {
        const wasDraft = !this.getCurrentThreadId();
        await this.syncMessagesFromServerBeforeSend();
        const result = await super.send(text, dataAction, true, actions, retry);
        if (result.isError)
        {
            return result;
        }
        await this.saveThreadIdToRepository();
        await this.saveMessagesToRepository();
        return result;
    }


    public async sendMessageWithoutSaving(text: string, dataAction: IChatActionsData | null, actions: IChatActionList): Promise<IChatMessageResult>
    {
        await this.syncMessagesFromServerBeforeSend();
        const result = await super.send(text, dataAction, false, actions);
        if (result.isError)
        {
            return result;
        }
        await this.saveThreadIdToRepository();
        return result;
    }

    // --- Secondary threads -------------------------------------------------

    public getThreadMessages(scope: string): Observable<IChatMessage[]>
    {
        return this.getScopedThreadState(scope).messages.pipe(
            map(messages => this.convertOfficeMessagesToMessages(messages)),
        );
    }

    public $isThreadSending(scope: string): Observable<boolean>
    {
        return this.getScopedThreadState(scope).isSending.asObservable();
    }

    /** Resolve the thread for the active document and load what it already holds. */
    public async initThread(scope: string): Promise<void>
    {
        const state = this.getScopedThreadState(scope);
        const previousThreadId = state.threadId;

        // Always re-resolve: the document — and with it the thread key — may
        // have changed since the last init.
        const threadId = await this.ensureScopedThread(scope, true);
        if (previousThreadId && previousThreadId !== threadId)
        {
            // Another document's thread: drop what belonged to the old one.
            state.messages.next([]);
        }

        const history = await this.fetchThreadHistory(threadId);
        state.lastAnswerMsgId = history.find(item => item.role === 'ASSISTANT')?.msg_id ?? null;
        // msgs/get returns newest first; the store is kept chronological.
        state.messages.next(this.convertHistoryToOfficeMessages(history.slice().reverse()));
    }

    /** Re-resolve every secondary thread a view has already opened. */
    private async refreshScopedThreads(): Promise<void>
    {
        for (const scope of this.getScopedThreadScopes())
        {
            try
            {
                await this.initThread(scope);
            }
            catch (e)
            {
                logger.error('chat: thread refresh failed', scope, e);
            }
        }
    }

    public async sendMessageToThread(scope: string, text: string): Promise<IChatMessageResult>
    {
        const state = this.getScopedThreadState(scope);
        const traceID = uuidV4() as IGuid;
        const timestamp = Date.now();

        if (state.isSending.getValue())
        {
            return { text: 'Request already in progress', isError: true, traceID, timestamp };
        }

        state.isSending.next(true);

        try
        {
            const answer = await this.sendToScopedThread(scope, text);
            state.messages.next([
                ...state.messages.getValue(),
                this.createScopedUserMessage(text),
                this.convertHistoryItemToOfficeMessage(answer),
            ]);

            return { text: answer.content.text, isError: false, traceID, timestamp };
        }
        catch (e)
        {
            logger.error('chat: thread send failed', e);
            return {
                text: (e instanceof Error ? e.message : describeError(e)) || 'Failed to send message',
                isError: true,
                traceID,
                timestamp,
            };
        }
        finally
        {
            state.isSending.next(false);
        }
    }

    // The prompt as it will come back from msgs/get on the next init, so the
    // in-memory thread matches a reloaded one.
    private createScopedUserMessage(text: string): IChatOfficeMessage
    {
        const id = uuidV4();
        return {
            sender: coreConstants.MESSAGE_SENDER.user,
            value: text,
            msg_id: id,
            localId: id,
            createdAt: Date.now(),
            isPending: true,
        };
    }

    public async clearMessages(): Promise<void>
    {
        // Empty the rendered model first so the UI clears immediately. The
        // thread delete (network) and local purge (IndexedDB) must not gate the
        // visual clear, otherwise the messages flash back while we wait on them.
        this.$messages.next([]);
        await this.clearCurrentThread();
        await this.chatRepository.deleteMessages();
    }

    public async deleteMessage(index: number): Promise<void>
    {
        const messages = this.$messages.getValue();
        if (index < 0 || index >= messages.length)
        {
            return;
        }

        const message = messages[ index ];

        // Drop it from the rendered model first so the UI updates immediately,
        // then delete it server-side (with its attachments) and from IndexedDB.
        const nextMessages = [ ...messages.slice(0, index), ...messages.slice(index + 1) ];
        this.$messages.next(nextMessages);
        this.setLastAnswerMsgIdFromOfficeMessages(nextMessages);
        await this.deleteMessageRemote(message);
        await this.chatRepository.deleteMessage(index);
    }

    public deleteLastUserMessage(): void
    {
        const messages = this.$messages.getValue();
        const lastUserIndex = messages.map((m, i) => m.sender === coreConstants.MESSAGE_SENDER.user ? i : -1).filter(i => i !== -1).pop();
        if (lastUserIndex !== undefined)
        {
            this.$messages.next([ ...messages.slice(0, lastUserIndex), ...messages.slice(lastUserIndex + 1) ]);
        }
    }

    public async writeBotMessage(text: string): Promise<void>
    {
        const botMessage = super.createBotMessage(text, false);
        const newMessages = this.$messages.getValue();
        newMessages.push(botMessage);
        this.$messages.next(newMessages);
        this.saveMessagesToRepository();
    }

    public async init(): Promise<void>
    {
        this.isMessagesLoading.next(true);
        await this.initCryptoServiceRepository();
        await this.initData();
    }

    private convertOfficeMessagesToMessages(data: IChatOfficeMessage[]): IChatMessage[]
    {
        return data.map(item =>
        {
            if (item.sender === coreConstants.MESSAGE_SENDER.user)
            {
                return {
                    value: item.value,
                    sender: coreConstants.MESSAGE_SENDER.user,
                    meta: item.meta,
                    attachments: item.attachments,
                    msg_id: item.msg_id,
                    localId: item.localId,
                    createdAt: item.createdAt,
                    model: item.model,
                    tokensUsage: item.tokensUsage,
                    isPending: item.isPending,
                };
            }

            return {
                value: item.value,
                sender: coreConstants.MESSAGE_SENDER.bot,
                attachments: item.attachments,
                msg_id: item.msg_id,
                localId: item.localId,
                createdAt: item.createdAt,
                model: item.model,
                tokensUsage: item.tokensUsage,
                isPending: item.isPending,
                options: {
                    plan: {
                        availableTokens: 0,
                        type: coreConstants.SETTING_PLAN_TYPE.TRIAL,
                    },
                    usedTotalTokens: 0,
                    usages: [],
                    attachments: item.attachments,
                }
            };
        });
    }

    private convertMessagesToOfficeMessages(data: IChatMessage[]): IChatOfficeMessage[]
    {
        return data.map(item =>
        {
            if (item.sender === coreConstants.MESSAGE_SENDER.user)
            {
                const msgId = item.msg_id ?? uuidV4();
                return {
                    value: item.value,
                    sender: coreConstants.MESSAGE_SENDER.user,
                    meta: item.meta,
                    attachments: item.attachments,
                    msg_id: msgId,
                    localId: item.localId ?? msgId,
                    createdAt: item.createdAt,
                    model: item.model,
                    tokensUsage: item.tokensUsage,
                    isPending: item.isPending,
                };
            }

            const value = this.chatUtils.getMessageValue(item);
            const isValueImg = this.chatUtils.isValueImg(item);
            const msgId = item.msg_id ?? uuidV4();
            return {
                value: value,
                sender: item.sender,
                isImage: isValueImg,
                attachments: item.attachments,
                // attachments: item.options.attachments,
                msg_id: msgId,
                localId: item.localId ?? msgId,
                createdAt: item.createdAt,
                model: item.model,
                tokensUsage: item.tokensUsage,
                isPending: item.isPending,
            };
        });
    }

    private async initRepository(documentID?: string): Promise<void>
    {
        const session = this.sessionService.getSession();
        const resolvedDocumentID = documentID ?? await this.resolvePrimaryRepositoryDocumentId();

        if (session.user_id && resolvedDocumentID)
        {
            this.chatRepository.defineHistoryKey(session.user_id, resolvedDocumentID);
        }
    }

    private async resolvePrimaryRepositoryDocumentId(): Promise<string>
    {
        const documentIDs = await this.resolveRepositoryDocumentIds();
        const [ primaryDocumentID ] = documentIDs;

        if (!primaryDocumentID)
        {
            throw new Error('Document id not found');
        }

        return primaryDocumentID;
    }

    private async resolveRepositoryDocumentIds(): Promise<string[]>
    {
        const contextDocumentID = this.threadManager.getContext()?.meta.id;
        const host = this.officeService.getHost();
        const documentIDs = contextDocumentID ? [ contextDocumentID ] : [];

        const hostDocumentID = await host.getDocumentIDAsync();
        if (hostDocumentID && !documentIDs.includes(hostDocumentID))
        {
            documentIDs.push(hostDocumentID);
        }

        if (documentIDs.length === 0)
        {
            throw new Error('Document id not found');
        }

        return documentIDs;
    }

    private async saveMessagesToRepository(forceRewrite = false, historyVersion?: number): Promise<void>
    {
        const chatMessagesOld = this.$messages.getValue();
        const chatMessages = this.convertOfficeMessagesToMessages(chatMessagesOld);
        const lastMessageIndex = chatMessages.length - 1;

        if (lastMessageIndex < 0)
        {
            if (forceRewrite)
            {
                await this.chatRepository.writeMessages([], historyVersion);
            }
            return;
        }

        if (!forceRewrite)
        {
            const repositoryMessages = await this.chatRepository.readMessages();
            if (repositoryMessages.length + 1 === chatMessages.length)
            {
                const lastMessage = chatMessages[ lastMessageIndex ];
                const encryptedMessage = await this.chatRepositoryCrypto.encryptMessage({
                    ...lastMessage,
                    createdAt: chatMessagesOld[ lastMessageIndex ]?.createdAt,
                });
                await this.chatRepository.appendMessage(encryptedMessage);
                return;
            }
        }

        const messagesWithTimestamp = chatMessages.map((message, index) => ({
            ...message,
            createdAt: chatMessagesOld[ index ]?.createdAt,
        }));
        const encryptedMessages = await this.chatRepositoryCrypto.encryptMessages(messagesWithTimestamp);
        await this.chatRepository.writeMessages(encryptedMessages, historyVersion);
    }

    private async saveThreadIdToRepository(): Promise<void>
    {
        await this.chatRepository.writeThreadId(this.getCurrentThreadId());
    }

    private async initData(): Promise<void>
    {
        this.isMessagesLoading.next(true);
        this.threadManager.reset();
        this.setCurrentThreadId(null);
        await this.initRepository();
        const migration = await this.migrateThreadIdIfNeeded();
        if (migration.blocked)
        {
            await this.initThreadId();
            await this.initOfficeMessagesFromRepository();
            this.isMessagesLoading.next(false);
            this.resumePendingAnswer();
            return;
        }

        const threadId = migration.threadId ?? await this.ensureCurrentThread(true);
        this.setCurrentThreadId(threadId);

        const initResult = await this.initOfficeMessages(threadId);
        if (initResult.loadedFromServer)
        {
            await this.chatRepository.writeThreadId(threadId);
            await this.refreshHistoryIfNeeded(initResult.serverHistory);
            if (migration.shouldFinalize)
            {
                await this.chatRepository.writeThreadVersion(THREAD_MIGRATION_VERSION);
            }
        }
        this.isMessagesLoading.next(false);
        this.resumePendingAnswer();
    }

    /**
     * A message sent in a previous session may have been accepted by the backend
     * without its answer ever being shown (the task pane was closed before the
     * poll finished). If a marker for the current thread survives and the answer
     * is still not in the loaded history, pick the poll back up in the
     * background and keep the composer in its loading state until it lands.
     */
    private resumePendingAnswer(): void
    {
        if (this.awaitingAnswer.getValue())
        {
            return;
        }

        const threadId = this.getCurrentThreadId();
        if (!threadId)
        {
            return;
        }

        const pending = this.pendingAnswerStore.read(threadId);
        if (!pending || pending.threadId !== threadId)
        {
            return;
        }

        const messages = this.$messages.getValue();
        const lastMessage = messages[ messages.length - 1 ];
        const answerAlreadyShown = !!lastMessage
            && lastMessage.sender === coreConstants.MESSAGE_SENDER.bot
            && lastMessage.msg_id !== pending.baselineAnswerMsgId;

        if (answerAlreadyShown)
        {
            this.pendingAnswerStore.clear(threadId);
            return;
        }

        void this.awaitPendingAnswer(threadId, pending.baselineAnswerMsgId);
    }

    private async awaitPendingAnswer(threadId: string, baselineAnswerMsgId: string | null): Promise<void>
    {
        this.awaitingAnswer.next(true);

        try
        {
            const answer = await this.fetchLatestAnswer(threadId, baselineAnswerMsgId);

            // Another init (document/session change) may have moved us on.
            if (this.getCurrentThreadId() !== threadId)
            {
                return;
            }

            this.setLastAnswerMessageId(answer.msg_id);

            const messages = this.$messages.getValue();
            if (messages.some(message => message.msg_id === answer.msg_id))
            {
                return;
            }

            const botMessage = this.createBotMessage(
                answer.text || '',
                answer.attachments.some(attachment =>
                    attachment.type === R2AttachmentType.Image
                    || attachment.mime_type?.toLowerCase().startsWith('image/')
                ),
                answer.attachments,
                answer.msg_id,
                answer.item,
            );

            const nextMessages = [ ...messages, botMessage ];
            this.$messages.next(nextMessages);
            this.setLastAnswerMsgIdFromOfficeMessages(nextMessages);
            await this.saveMessagesToRepository(true);
        }
        catch (e)
        {
            logger.error('resumePendingAnswer: failed to fetch answer', e);
        }
        finally
        {
            this.pendingAnswerStore.clear(threadId);
            this.awaitingAnswer.next(false);
        }
    }

    private async initThreadId(): Promise<void>
    {
        this.setCurrentThreadId(await this.chatRepository.readThreadId());
    }

    private async initOfficeMessages(threadId: string): Promise<IOfficeMessagesInitResult>
    {
        try
        {
            const repositoryDocumentIds = await this.resolveRepositoryDocumentIds();

            const serverHistory = await this.loadChatHistoryFromServer(threadId);

            const localMessages = await this.readOfficeMessagesFromRepositoryWithFallback(repositoryDocumentIds);
            const serverMessages = this.convertHistoryToOfficeMessages(serverHistory);

            if (serverMessages.length === 0 && localMessages.length > 0)
            {
                this.$messages.next(localMessages);
                this.setLastAnswerMsgIdFromOfficeMessages(localMessages);
                return { serverHistory, loadedFromServer: true };
            }

            if (serverMessages.length === 0)
            {
                this.$messages.next([]);
                this.setLastAnswerMsgIdFromOfficeMessages([]);
                await this.chatRepository.writeMessages([]);
                return { serverHistory, loadedFromServer: true };
            }

            const serverLastMessage = this.getNewestHistoryItem(serverHistory);
            const localLastMessage = localMessages.length > 0 ? localMessages[ localMessages.length - 1 ] : null;

            if (serverLastMessage?.msg_id && serverLastMessage.msg_id === localLastMessage?.msg_id)
            {
                const mergedMessages = this.mergeServerMessageDetails(localMessages, serverMessages);
                const repaired = this.repairCachedAssistantValues(mergedMessages.messages, serverHistory);
                this.$messages.next(repaired.messages);
                this.setLastAnswerMsgIdFromOfficeMessages(repaired.messages);
                if (mergedMessages.changed || repaired.changed)
                {
                    await this.saveMessagesToRepository(true);
                }
                return { serverHistory, loadedFromServer: true };
            }

            if (localLastMessage?.msg_id)
            {
                const newServerMessages = this.getNewMessagesAfterLocalLastMessage(serverMessages, localMessages, localLastMessage.msg_id);
                if (newServerMessages)
                {
                    const repaired = this.repairCachedAssistantValues(localMessages, serverHistory);
                    const mergedMessages = [ ...repaired.messages, ...newServerMessages ];
                    this.$messages.next(mergedMessages);
                    this.setLastAnswerMsgIdFromOfficeMessages(mergedMessages);
                    if (repaired.changed)
                    {
                        await this.saveMessagesToRepository(true);
                    }
                    else
                    {
                        await this.appendOfficeMessagesToRepository(newServerMessages);
                    }
                    return { serverHistory, loadedFromServer: true };
                }
            }

            this.$messages.next(serverMessages);
            this.setLastAnswerMsgIdFromOfficeMessages(serverMessages);
            await this.saveMessagesToRepository(true);
            return { serverHistory, loadedFromServer: true };
        } catch (e)
        {
            logger.error(e);
            await this.initOfficeMessagesFromRepository();
            return { serverHistory: [], loadedFromServer: false };
        }
    }

    private async migrateThreadIdIfNeeded(): Promise<IThreadMigrationResult>
    {
        const current = await this.chatRepository.readThreadVersionAndThreadId();
        if (current.threadVersion === THREAD_MIGRATION_VERSION)
        {
            return { threadId: null, shouldFinalize: false, blocked: false };
        }

        if (!current.threadId)
        {
            return { threadId: null, shouldFinalize: true, blocked: false };
        }

        try
        {
            return {
                threadId: await this.assignCurrentThreadId(current.threadId),
                shouldFinalize: true,
                blocked: false,
            };
        }
        catch (e)
        {
            const isAlreadyAssignedError = e instanceof HttpErrorResponse
                && e.status === 403
                && e.error?.details?.result === R2ApiResult.FailAccessDenied;

            if (e instanceof HttpErrorResponse && !isAlreadyAssignedError)
            {
                logger.error('Failed to migrate thread id', e);
                return { threadId: null, shouldFinalize: false, blocked: true };
            }

            logger.warn('Thread id is already assigned; resolving the current thread', e);
            try
            {
                return {
                    threadId: await this.ensureCurrentThread(true),
                    shouldFinalize: true,
                    blocked: false,
                };
            }
            catch (ensureError)
            {
                logger.error('Failed to resolve the current thread', ensureError);
                return { threadId: null, shouldFinalize: false, blocked: true };
            }
        }
    }

    private async refreshHistoryIfNeeded(serverHistory: IR2ChatHistoryItem[]): Promise<void>
    {
        const shouldRefreshHistory = await this.chatRepository.readHistoryVersion() !== environment.historyVersion;
        if (!shouldRefreshHistory || serverHistory.length === 0)
        {
            return;
        }

        await this.applyServerHistory(serverHistory);
    }

    private async syncMessagesFromServerBeforeSend(): Promise<void>
    {
        const threadId = this.getCurrentThreadId();
        if (!threadId)
        {
            return;
        }

        try
        {
            const serverHistory = await this.loadChatHistoryFromServer(threadId);
            if (serverHistory.length === 0)
            {
                return;
            }

            await this.appendNewServerHistory(serverHistory);
        }
        catch (e)
        {
            logger.error('syncMessagesFromServerBeforeSend failed', e);
        }
    }

    private async appendNewServerHistory(serverHistory: IR2ChatHistoryItem[]): Promise<void>
    {
        const currentMessages = this.$messages.getValue();
        const serverMessages = this.convertHistoryToOfficeMessages(serverHistory);
        const newMessages = await this.getNewServerMessages(serverMessages, currentMessages);
        const repaired = this.repairCachedAssistantValues(currentMessages, serverHistory);
        if (newMessages.length === 0 && !repaired.changed)
        {
            return;
        }

        const nextMessages = [ ...repaired.messages, ...newMessages ];
        this.$messages.next(nextMessages);
        this.setLastAnswerMsgIdFromOfficeMessages(nextMessages);

        if (repaired.changed)
        {
            await this.saveMessagesToRepository(true);
        }
        else
        {
            await this.appendOfficeMessagesToRepository(newMessages);
        }
    }

    private async getNewServerMessages(serverMessages: IChatOfficeMessage[], currentMessages: IChatOfficeMessage[]): Promise<IChatOfficeMessage[]>
    {
        const currentMsgIds = new Set(currentMessages.map(message => message.msg_id));
        const lastCurrentMessage = await this.getLastOfficeMessage();
        const lastCurrentServerIndex = lastCurrentMessage
            ? serverMessages.findIndex(message => message.msg_id === lastCurrentMessage.msg_id)
            : -1;

        if (lastCurrentServerIndex >= 0)
        {
            return serverMessages
                .slice(lastCurrentServerIndex + 1)
                .filter(message => !currentMsgIds.has(message.msg_id));
        }

        return serverMessages.filter(message => !currentMsgIds.has(message.msg_id));
    }

    private getNewMessagesAfterLocalLastMessage(
        serverMessages: IChatOfficeMessage[],
        localMessages: IChatOfficeMessage[],
        localLastMessageId: string,
    ): IChatOfficeMessage[] | null
    {
        const localMsgIds = new Set(localMessages.map(message => message.msg_id));
        const localLastServerIndex = serverMessages.findIndex(message => message.msg_id === localLastMessageId);
        if (localLastServerIndex < 0)
        {
            return null;
        }

        return serverMessages
            .slice(localLastServerIndex + 1)
            .filter(message => !localMsgIds.has(message.msg_id));
    }

    /** Backfills response metadata added after a message was first cached locally. */
    private mergeServerMessageDetails(
        localMessages: IChatOfficeMessage[],
        serverMessages: IChatOfficeMessage[],
    ): { messages: IChatOfficeMessage[]; changed: boolean; }
    {
        const serverById = new Map(serverMessages.map(message => [ message.msg_id, message ]));
        let changed = false;

        const messages = localMessages.map((localMessage) =>
        {
            const serverMessage = serverById.get(localMessage.msg_id);
            if (!serverMessage)
            {
                return localMessage;
            }

            const model = serverMessage.model ?? localMessage.model;
            const tokensUsage = serverMessage.tokensUsage ?? localMessage.tokensUsage;
            const createdAt = serverMessage.createdAt ?? localMessage.createdAt;
            const serverMeta = serverMessage.sender === coreConstants.MESSAGE_SENDER.user
                ? serverMessage.meta
                : undefined;
            const localMeta = localMessage.sender === coreConstants.MESSAGE_SENDER.user
                ? localMessage.meta
                : undefined;
            const meta = localMeta ?? serverMeta;

            if (
                model === localMessage.model
                && tokensUsage === localMessage.tokensUsage
                && createdAt === localMessage.createdAt
                && meta === localMeta
            )
            {
                return localMessage;
            }

            changed = true;
            return localMessage.sender === coreConstants.MESSAGE_SENDER.user
                ? { ...localMessage, createdAt, model, tokensUsage, meta }
                : { ...localMessage, createdAt, model, tokensUsage };
        });

        return { messages, changed };
    }

    private repairCachedAssistantValues(
        localMessages: IChatOfficeMessage[],
        serverHistory: IR2ChatHistoryItem[],
    ): { messages: IChatOfficeMessage[]; changed: boolean; }
    {
        const serverById = new Map(serverHistory.map(message => [ message.msg_id, message ]));
        let changed = false;
        const messages = localMessages.map((localMessage) =>
        {
            const serverMessage = serverById.get(localMessage.msg_id);
            const fallback = serverMessage?.meta?.skill_invocation_source ?? serverMessage?.meta?.original_text;
            if (
                localMessage.sender !== coreConstants.MESSAGE_SENDER.bot
                || serverMessage?.role !== 'ASSISTANT'
                || !fallback
                || !/\[skill:[1-9]\d*\]/.test(fallback)
                || localMessage.value !== fallback
                || !serverMessage.content.text
                || localMessage.value === serverMessage.content.text
            )
            {
                return localMessage;
            }

            changed = true;
            return { ...localMessage, value: serverMessage.content.text };
        });

        return { messages, changed };
    }

    private async applyServerHistory(serverHistory: IR2ChatHistoryItem[]): Promise<void>
    {
        const serverMessages = this.convertHistoryToOfficeMessages(serverHistory);
        this.$messages.next(serverMessages);
        this.setLastAnswerMsgIdFromOfficeMessages(serverMessages);
        await this.saveMessagesToRepository(true, environment.historyVersion);
    }

    private async initOfficeMessagesFromRepository(): Promise<void>
    {
        try
        {
            const officeMessages = await this.readOfficeMessagesFromRepository();
            this.$messages.next(officeMessages);
            this.setLastAnswerMsgIdFromOfficeMessages(officeMessages);
        } catch (e)
        {
            logger.error(e);
            this.$messages.next([]);
            this.setLastAnswerMsgIdFromOfficeMessages([]);
        }
    }

    private async readOfficeMessagesFromRepository(): Promise<IChatOfficeMessage[]>
    {
        const messages = await this.chatRepository.readMessages();
        const decryptedMessages = await this.chatRepositoryCrypto.decryptMessages(messages);
        const officeMessages = this.convertMessagesToOfficeMessages(decryptedMessages);
        officeMessages.forEach((officeMessage, index) =>
        {
            officeMessage.createdAt = decryptedMessages[ index ]?.createdAt;
        });
        return officeMessages;
    }

    private async readOfficeMessagesFromRepositoryWithFallback(documentIDs: string[]): Promise<IChatOfficeMessage[]>
    {
        const session = this.sessionService.getSession();
        const userID = session.user_id;
        const [ primaryDocumentID ] = documentIDs;

        if (!userID || !primaryDocumentID)
        {
            return this.readOfficeMessagesFromRepository();
        }

        for (const documentID of documentIDs)
        {
            this.chatRepository.defineHistoryKey(userID, documentID);
            const officeMessages = await this.readOfficeMessagesFromRepository();
            if (officeMessages.length === 0)
            {
                continue;
            }

            if (documentID !== primaryDocumentID)
            {
                this.chatRepository.defineHistoryKey(userID, primaryDocumentID);
                await this.saveRepositoryMessages(officeMessages, true);
            }
            else
            {
                this.chatRepository.defineHistoryKey(userID, primaryDocumentID);
            }

            return officeMessages;
        }

        this.chatRepository.defineHistoryKey(userID, primaryDocumentID);
        return [];
    }

    private async saveRepositoryMessages(messages: IChatOfficeMessage[], forceRewrite = false): Promise<void>
    {
        const previousMessages = this.$messages.getValue();
        this.$messages.next(messages);
        try
        {
            await this.saveMessagesToRepository(forceRewrite);
        }
        finally
        {
            this.$messages.next(previousMessages);
        }
    }

    private async appendOfficeMessagesToRepository(messages: IChatOfficeMessage[]): Promise<void>
    {
        for (const message of messages)
        {
            const chatMessage = this.convertOfficeMessagesToMessages([ message ])[ 0 ];
            const encryptedMessage = await this.chatRepositoryCrypto.encryptMessage({
                ...chatMessage,
                createdAt: message.createdAt,
            });
            await this.chatRepository.appendMessage(encryptedMessage);
        }
    }

    private async loadChatHistoryFromServer(threadId: string): Promise<IR2ChatHistoryItem[]>
    {
        const pageSize = 100;
        const history: IR2ChatHistoryItem[] = [];
        let lastMsgId: string | undefined;
        let page: IR2ChatHistoryItem[];

        do
        {
            const response = await this.dependencies.sessionCrypto.postAddin<unknown, IR2GetMessagesResponse>(
                '/msgs/get',
                {
                    thread_id: threadId,
                    page_size: pageSize,
                    msg_order: 'desc',
                    ...(lastMsgId ? { last_msg_id: lastMsgId } : {}),
                },
            );
            page = response.chat_history ?? [];
            if (page.length === 0)
            {
                break;
            }

            history.push(...page);
            const nextLastMsgId = page[ page.length - 1 ]?.msg_id;
            if (!nextLastMsgId || nextLastMsgId === lastMsgId)
            {
                break;
            }

            lastMsgId = nextLastMsgId;
        }
        while (page.length === pageSize);

        return history.reverse();
    }

    private convertHistoryToOfficeMessages(history: IR2ChatHistoryItem[]): IChatOfficeMessage[]
    {
        return history
            .map((message) => this.convertHistoryItemToOfficeMessage(message));
    }

    private getNewestHistoryItem(history: IR2ChatHistoryItem[]): IR2ChatHistoryItem | null
    {
        return history.reduce<IR2ChatHistoryItem | null>((newestMessage, message) =>
        {
            if (!newestMessage || message.dt > newestMessage.dt)
            {
                return message;
            }

            return newestMessage;
        }, null);
    }

    private async getLastOfficeMessage(): Promise<IChatOfficeMessage | null>
    {
        const messages = await this.chatRepository.readMessages();
        const lastMessage = messages.length > 0 ? messages[ messages.length - 1 ] : null;
        if (!lastMessage)
        {
            return null;
        }

        const decryptedMessages = await this.chatRepositoryCrypto.decryptMessages([ lastMessage ]);
        const officeMessages = this.convertMessagesToOfficeMessages(decryptedMessages);
        const officeMessage = officeMessages[ 0 ] ?? null;
        if (officeMessage)
        {
            officeMessage.createdAt = decryptedMessages[ 0 ]?.createdAt;
        }

        return officeMessage;
    }

    private convertHistoryItemToOfficeMessage(message: IR2ChatHistoryItem): IChatOfficeMessage
    {
        const attachments = message.attachments ?? [];
        if (message.role === 'USER')
        {
            return {
                sender: coreConstants.MESSAGE_SENDER.user,
                value: this.resolveHistoryMessageValue(message),
                meta: message.meta,
                attachments,
                msg_id: message.msg_id,
                localId: message.msg_id,
                createdAt: message.dt * 1000,
            };
        }

        return {
            sender: coreConstants.MESSAGE_SENDER.bot,
            value: message.content.text,
            isImage: attachments.some((attachment) =>
                attachment.type === R2AttachmentType.Image
                || attachment.mime_type?.toLowerCase().startsWith('image/')
            ),
            localId: message.msg_id,
            attachments,
            msg_id: message.msg_id,
            createdAt: message.dt * 1000,
            model: message.model,
            tokensUsage: message.tokens_usage,
        };
    }

    private resolveHistoryMessageValue(message: IR2ChatHistoryItem): string
    {
        return message.meta?.skill_invocation_source
            ?? message.meta?.original_text
            ?? message.content.text;
    }

    private defineHandlerForDocumentChange(): void
    {
        const host = this.officeService.getHost();
        host.onDocumentChange(async () =>
        {
            await this.initData();
            await this.refreshScopedThreads();
        });
    }

    private defineHandlerForSessionChange(): void
    {
        this.sessionService.sessionInfo$.subscribe(
            this.handlerForSessionChange.bind(this)
        );
    }

    private handlerForSessionChange(info: ISessionInfo | null): void
    {
        if (info && info.user_id)
        {
            logger.log(info, info.user_id);
            this.init();
        }
    }

    private async initCryptoServiceRepository(): Promise<void>
    {
        const sessionInfo = this.sessionService.getSession();
        await this.cryptoServiceRepository.ApplySecurityCode(`chatRepository_${ sessionInfo.user_id }`);

        // this.sessionService.sessionInfo$.subscribe(async (sessionInfo) => {
        //     if (sessionInfo) {
        //         await this.cryptoServiceRepository.ApplySecurityCode(`chatRepository_${sessionInfo.user_id}`)
        //     }
        // });
    }

    onDestroy(): void { }
}
